import React, {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import * as authProvider from './auth-provider';
import {
  Clock,
  ClockFields,
  ClockSurface,
  resolveClock,
  surfaceOfPath,
  usableZone,
} from '../function-utils/building-time';

/* ──────────────────────────  WHOSE CLOCK, IN REACT  ──────────────────────────
 *
 * The rule itself is resolveClock() in function-utils/building-time. This file
 * is the three things a component needs to call it without threading props:
 *
 *   - the SURFACE a component is drawn on (desk shell, resident portal, Super
 *     Admin console, or the rest of the app) — a context the shells provide;
 *   - the ZONE OF A BUILDING, by id — a small store the buildings list feeds;
 *   - useClock(), which puts those together with the signed-in role.
 *
 * A component shared by a desk screen and a dashboard screen (the record
 * detail, the activity feed, the roster, the IQ chat) calls useClock() and so
 * changes clock with the surface it is drawn on.
 */

const SurfaceContext = createContext<ClockSurface>('app');

/* THE SIGNED-IN ROLE, read without requiring a provider. `useAuth()` throws
   outside <AuthProvider>, and a clock must never be the reason a screen cannot
   render — so the context is read directly and an absent one means "no role"
   (resolveClock then answers the building's clock, the safe default).

   The second branch is for a host that supplies `useAuth` and no context
   object (every jest suite that stubs the auth module does). Which branch runs
   is fixed for the life of the module, so the hook order never changes. */
type AuthShape = { user?: { role?: string | null } | null } | undefined;
const NoAuth = createContext<AuthShape>(undefined);
const authContext = (authProvider as { AuthContext?: React.Context<AuthShape> }).AuthContext;
const stubbedUseAuth = authContext
  ? null
  : (authProvider as { useAuth?: () => AuthShape }).useAuth || null;

const useSignedInRole = (): string | null => {
  const fromContext = useContext(authContext || NoAuth);
  let auth: AuthShape = fromContext;
  if (stubbedUseAuth) {
    try {
      auth = stubbedUseAuth();
    } catch {
      auth = undefined;
    }
  }

  return auth?.user?.role ?? null;
};

/** The role the clock rule is applied with — for a component that needs it
 *  outside useClock (the notification bell resolves a clock per row). */
export const useClockRole = (): string | null => useSignedInRole();

export const ClockSurfaceProvider: React.FC<{
  surface: ClockSurface;
  children: React.ReactNode;
}> = ({ surface, children }) => (
  <SurfaceContext.Provider value={surface}>{children}</SurfaceContext.Provider>
);

export const useClockSurface = (): ClockSurface => useContext(SurfaceContext);

// The path rule lives with the rest of the clock rule; kept exported from here
// for the components that already import it from the context module.
export { surfaceOfPath };

/* ───────────────────────  The zone of a building, by id  ─────────────────── */

type ZoneListener = () => void;

const zonesById = new Map<string, string>();
const listeners = new Set<ZoneListener>();
let version = 0;

/**
 * Feed the store from a buildings list (use-get-building-data does, on every
 * load). Additive: the desk router and the dashboard each hold their own list,
 * and a building one of them knows must not be forgotten when the other loads.
 */
export const publishBuildingZones = (
  buildings: ReadonlyArray<{ id?: string | number | null; timezone?: string | null }>
): void => {
  let changed = false;
  buildings.forEach(building => {
    if (building.id === null || building.id === undefined) return;
    const id = String(building.id);
    const zone = usableZone(building.timezone);
    if ((zonesById.get(id) || '') !== zone) {
      if (zone) zonesById.set(id, zone);
      else zonesById.delete(id);
      changed = true;
    }
  });
  if (changed) {
    version += 1;
    listeners.forEach(listener => listener());
  }
};

/** A building's own zone, or '' when the lists loaded so far do not hold it. */
export const zoneOfBuilding = (buildingId?: string | number | null): string =>
  buildingId === null || buildingId === undefined || buildingId === ''
    ? ''
    : zonesById.get(String(buildingId)) || '';

/** Test seam: empty the store between tests. */
export const resetBuildingZones = (): void => {
  zonesById.clear();
  version += 1;
  listeners.forEach(listener => listener());
};

const subscribe = (listener: ZoneListener): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const snapshot = (): number => version;

/**
 * A building's OWN zone, looked up by id. (Not `useBuildingZone` — that name is
 * the building FORM's zone state, in building-time-zone.tsx.)
 */
export const useZoneOfBuilding = (buildingId?: string | number | null): string => {
  useSyncExternalStore(subscribe, snapshot, snapshot);

  return zoneOfBuilding(buildingId);
};

/* ────────────────────────────────  useClock  ─────────────────────────────── */

export interface UseClockOptions {
  /** The building the screen is about, to look its zone up. */
  buildingId?: string | number | null;
  /** That building's zone, when the caller already holds it (a row's `tz`, the
   *  /logs `window.tz`, the roster's `day_tz`). Wins over the lookup. */
  buildingZone?: string | null;
  /** The response on screen: its `tz` and `tz_source`. */
  response?: ClockFields | null;
  /** Draw as if on another surface (a preview of a sent, building-clock
   *  document inside a dashboard, for instance). */
  surface?: ClockSurface;
}

/**
 * The clock THIS component formats in, and what to call it: surface, then
 * response, then role (resolveClock). Pass the result to `<ClockNote>`.
 */
export const useClock = (options: UseClockOptions = {}): Clock => {
  const surfaceFromContext = useClockSurface();
  const role = useSignedInRole();
  const looked = useZoneOfBuilding(options.buildingId);
  const surface = options.surface || surfaceFromContext;
  const buildingZone = usableZone(options.buildingZone) || looked;
  const tz = options.response?.tz ?? null;
  const tzSource = options.response?.tz_source ?? null;

  return useMemo(
    () =>
      resolveClock({
        surface,
        role,
        buildingZone,
        response: tz || tzSource ? { tz, tz_source: tzSource } : null,
      }),
    [surface, role, buildingZone, tz, tzSource]
  );
};
